import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { withLatestFrom, switchMap, catchError, of, map } from "rxjs";
import { CustomerClient } from "src/api-service";
import { selectSelectedCompany } from "../../../user/user.selectors";
import { selectCustomerState } from "../customer.selectors";
import { CustomersActions } from "./customers.actions";
import { normalizeError } from "../../../app-store-error.types";

// ============================================================================
// CUSTOMERS LIST EFFECTS - Single effect, no chained actions
// ============================================================================

@Injectable()
export class CustomersEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly customerClient = inject(CustomerClient);

    load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomersActions.load),
            withLatestFrom(
                this.store.select(selectSelectedCompany),
                this.store.select(selectCustomerState),
            ),
            switchMap(([action, selectedCompany, state]) => {
                const companyId = selectedCompany?.id;
                if (!companyId) {
                    return of(CustomersActions.loadFailure({
                        error: { message: "No company selected" },
                        toastKey: "CUSTOMER_LIST.TOAST.LOAD_ERROR",
                    }));
                }

                // Merge: action params → current state → defaults
                const params = {
                    pagination: {
                        pageIndex: action.pagination?.pageIndex ?? state?.pageNumber ?? 1,
                        pageSize: action.pagination?.pageSize ?? state?.pageSize ?? 50,
                    },
                    sorting: {
                        sortProperty: action.sorting?.sortProperty ?? state?.sortProperty ?? "code",
                        sortAscending: action.sorting?.sortAscending ?? state?.sortAscending ?? false,
                    },
                    searchFilter: action.searchFilter ?? state?.searchFilter ?? "",
                };

                return this.customerClient
                    .getFiltered(
                        companyId,
                        params.pagination.pageIndex,
                        params.pagination.pageSize,
                        params.searchFilter,
                        params.sorting.sortProperty,
                        params.sorting.sortAscending,
                        null,
                    )
                    .pipe(
                        map(response => CustomersActions.loadSuccess({
                            customers: response,
                            params,
                        })),
                        catchError(error => of(CustomersActions.loadFailure({
                            error: normalizeError(error),
                            toastKey: "CUSTOMER_LIST.TOAST.LOAD_ERROR",
                        }))),
                    );
            }),
        ),
    );
}
